import { useModalHooks } from "App/State/Hooks/useModalHooks";
import { selectClientValue } from "App/State/Slices/SelectedClientSlice";
import { useAppSelector } from "App/State/Store";
import { IFeatureAttributeDto } from "App/Typings/AbacFeatureAttribute";
import { IFeatureClientAttributeDto, ISyncClientFeature } from "App/Typings/AbacFeatureClientAttribute";
import { ITableColumn } from "App/Typings/Table";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  useGetAllFeatureVariantsQuery,
  useGetClientFeatures,
  useSyncFeatureClientAttributes,
} from "../../AbacAppQueries";

export interface IAbacClientTableRowProps extends Record<string, any> {
  appName: string;
  appId: string;
  featureName: string;
  featureId: string;
  distribution: string;
}

const columns: ITableColumn[] = [
  { label: "Application", propName: "appName" },
  { label: "Feature", propName: "featureName" },
  { label: "Distribution Type", propName: "distribution" },
  { label: "", propName: "" },
];

export default function useAbacClientState() {
  const { id: baseClientId, clientName } = useAppSelector(selectClientValue);
  const { data: features = [], isLoading } = useGetAllFeatureVariantsQuery();
  const { data: clientFeatures = [], isLoading: isClientFeaturesLoading } =
    useGetClientFeatures(baseClientId);
  const { mutateAsync: syncClientFeature, isLoading: isSavingClientFeature } =
    useSyncFeatureClientAttributes();

  const [appName, setAppName] = useState<string | null>(null);
  const [selectedFeature, setSelectedFeature] = useState<string | null>(null);
  const [selectedDistributionType, setSelectedDistributionType] = useState<"Default" | "Custom">("Default");
  const [defaultDistribution, setDefaultDistribution] = useState<IFeatureAttributeDto[]>([]);
  const [draftDistribution, setDraftDistribution] = useState<IFeatureAttributeDto[]>([]);
  const selectedFeatureName = useMemo(() => {
    const feature = features.find((f) => f.id === selectedFeature);
    return feature ? feature.name : "";
  }, [features, selectedFeature]);

  function setDefaultVariations(featureId: string) {
    const defaultFeature = features.find((f) => f.id === featureId);
    setSelectedDistributionType("Default");
    setDraftDistribution(defaultFeature?.defaultFeatureAttributes ?? []);
  }

  const featureClientAttributesToFeatureAttributes = useCallback(
    (featureId: string, featureClientAttributes: IFeatureClientAttributeDto[]): IFeatureAttributeDto[] => {
      const baseFeature = features.find((f) => f.id === featureId);
      if (!baseFeature) {
        return [];
      }
      const featureClientAttributesMap = new Map(
        featureClientAttributes.map((attr) => [attr.featureAttributeId, attr])
      );
      return (
        baseFeature.defaultFeatureAttributes?.map((attr) => {
          const clientAttr = featureClientAttributesMap.get(attr.id);
          return {
            ...attr,
            id: clientAttr ? clientAttr.featureAttributeId : attr.id,
            distribution: clientAttr ? clientAttr.distribution : attr.distribution,
            attributeKey: clientAttr ? clientAttr.key : attr.attributeKey,
          } as IFeatureAttributeDto;
        }) ?? []
      );
    },
    [features]
  );

  const handleFeatureSelect = useCallback(
    (featureId: string | null) => {
      setSelectedFeature(featureId);
      const feature = features.find((f) => f.id === featureId);
      const clientFeature = clientFeatures.find((cf) => cf.featureId === feature?.id);
      if (clientFeature) {
        setSelectedDistributionType("Custom");
        setDraftDistribution(
          featureClientAttributesToFeatureAttributes(featureId ?? "", clientFeature.featureClientAttributes)
        );
      } else {
        setDefaultVariations(featureId ?? "");
      }
      setAppName(feature?.appName ?? null);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [features, clientFeatures, featureClientAttributesToFeatureAttributes]
  );

  const handleUpdateDraftVariations = (updatedVariations: IFeatureAttributeDto[]) => {
    setDraftDistribution(updatedVariations);
  };

  const handleFeatureClose = () => {
    setSelectedDistributionType("Default");
    setSelectedFeature(null);
  };

  const handleChangeModalDistributionType = (type: "Default" | "Custom") => {
    setSelectedDistributionType(type);
  };

  const { closeModal } = useModalHooks();
  async function onSubmitClientFeature(values: IFeatureAttributeDto[]) {
    if (!isSavingClientFeature) {
      const clientFeature = clientFeatures.find((cf) => cf.featureId === selectedFeature);
      const command = {
        baseClientId,
        featureId: selectedFeature ?? "",
        featureClientAttributes: values.map((item) => ({
          id:
            clientFeature?.featureClientAttributes.find((fca) => fca.featureAttributeId === item.id)?.id ??
            null,
          featureAttributeId: item.id,
          distribution: item.distribution,
        })),
      } as ISyncClientFeature;

      await syncClientFeature(command, {
        onSuccess: () => {
          handleFeatureClose();
          closeModal();
        },
      });
    }
  }

  const tableData = useMemo(
    () =>
      features.map(
        (feature) =>
          ({
            appName: feature.appName,
            appId: feature.appId,
            featureName: feature.name,
            featureId: feature.id,
            distribution: !!clientFeatures.find((cf) => cf.featureId === feature.id) ? "Custom" : "Default",
          } as IAbacClientTableRowProps)
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [JSON.stringify(features), JSON.stringify(clientFeatures)]
  );

  useEffect(() => {
    if (selectedFeature) {
      const feature = features.find((f) => f.id === selectedFeature);
      if (feature) {
        setDefaultDistribution(feature.defaultFeatureAttributes ?? []);
      }
    }
  }, [selectedFeature, features]);

  return {
    getters: {
      appName,
      selectedFeatureName,
      selectedDistributionType,
      draftDistribution,
      tableData,
      clientName,
      columns,
      isLoading: isLoading || isClientFeaturesLoading,
      defaultDistribution,
      isSavingClientFeature,
    },
    handlers: {
      handleFeatureSelect,
      handleChangeModalDistributionType,
      handleUpdateDraftVariations,
      onSubmitClientFeature,
    },
  };
}
